import * as MutationHelpers from 'shared/helpers/vuex/mutationHelpers';
import types from '../mutation-types';
import KanbanBoardsAPI from '../../api/kanbanBoards';

export const state = {
  records: [],
  uiFlags: {
    isFetching: false,
    isFetchingBoard: false,
    isCreating: false,
    isUpdating: false,
    isDeleting: false,
  },
  currentBoard: null,
  currentBoardData: null,
};

export const getters = {
  getKanbanBoards(_state) {
    return _state.records;
  },
  getUIFlags(_state) {
    return _state.uiFlags;
  },
  getCurrentBoard(_state) {
    return _state.currentBoard;
  },
  getCurrentBoardData(_state) {
    return _state.currentBoardData;
  },
  getKanbanBoardById: _state => id => {
    return _state.records.find(record => record.id === id);
  },
};

export const actions = {
  get: async function getKanbanBoards({ commit }) {
    commit(types.SET_KANBAN_UI_FLAG, { isFetching: true });
    try {
      const response = await KanbanBoardsAPI.get();
      commit(types.SET_KANBAN_BOARDS, response.data);
    } catch (error) {
      // Ignore error
    } finally {
      commit(types.SET_KANBAN_UI_FLAG, { isFetching: false });
    }
  },

  show: async function showKanbanBoard({ commit }, id) {
    commit(types.SET_KANBAN_UI_FLAG, { isFetching: true });
    try {
      const response = await KanbanBoardsAPI.show(id);
      commit(types.SET_CURRENT_KANBAN_BOARD, response.data);
      return response.data;
    } finally {
      commit(types.SET_KANBAN_UI_FLAG, { isFetching: false });
    }
  },

  getBoardData: async function getBoardData({ commit }, { boardId, filters }) {
    commit(types.SET_KANBAN_UI_FLAG, { isFetchingBoard: true });
    try {
      const response = await KanbanBoardsAPI.getOptimizedBoardData(
        boardId,
        filters
      );
      commit(types.SET_CURRENT_KANBAN_BOARD_DATA, response.data);
      return response.data;
    } finally {
      commit(types.SET_KANBAN_UI_FLAG, { isFetchingBoard: false });
    }
  },

  create: async function createKanbanBoard({ commit }, boardData) {
    commit(types.SET_KANBAN_UI_FLAG, { isCreating: true });
    try {
      const response = await KanbanBoardsAPI.create(boardData);
      commit(types.ADD_KANBAN_BOARD, response.data);
      return response.data;
    } finally {
      commit(types.SET_KANBAN_UI_FLAG, { isCreating: false });
    }
  },

  update: async function updateKanbanBoard({ commit }, { id, ...boardData }) {
    commit(types.SET_KANBAN_UI_FLAG, { isUpdating: true });
    try {
      const response = await KanbanBoardsAPI.update(id, boardData);
      commit(types.EDIT_KANBAN_BOARD, response.data);
      return response.data;
    } finally {
      commit(types.SET_KANBAN_UI_FLAG, { isUpdating: false });
    }
  },

  delete: async function deleteKanbanBoard({ commit }, id) {
    commit(types.SET_KANBAN_UI_FLAG, { isDeleting: true });
    try {
      await KanbanBoardsAPI.delete(id);
      commit(types.DELETE_KANBAN_BOARD, id);
    } finally {
      commit(types.SET_KANBAN_UI_FLAG, { isDeleting: false });
    }
  },

  createColumn: async function createColumn(
    { commit },
    { boardId, columnData }
  ) {
    const response = await KanbanBoardsAPI.createColumn(boardId, columnData);
    commit(types.ADD_KANBAN_COLUMN, response.data);
    return response.data;
  },

  updateColumn: async function updateColumn(
    { commit },
    { boardId, columnId, columnData }
  ) {
    const response = await KanbanBoardsAPI.updateColumn(
      boardId,
      columnId,
      columnData
    );
    commit(types.EDIT_KANBAN_COLUMN, response.data);
    return response.data;
  },

  deleteColumn: async function deleteColumn({ commit }, { boardId, columnId }) {
    await KanbanBoardsAPI.deleteColumn(boardId, columnId);
    commit(types.DELETE_KANBAN_COLUMN, columnId);
  },

  reorderColumns: async function reorderColumns(
    { commit },
    { boardId, positions }
  ) {
    await KanbanBoardsAPI.reorderColumns(boardId, positions);
    commit(types.REORDER_KANBAN_COLUMNS, positions);
  },

  moveConversation: async function moveConversation(
    { commit },
    { boardId, columnId, conversationId }
  ) {
    const response = await KanbanBoardsAPI.moveConversation(
      boardId,
      columnId,
      conversationId
    );
    // Actualizar el estado optimistamente
    commit(types.MOVE_KANBAN_CONVERSATION, { columnId, conversationId });
    return response.data;
  },
};

export const mutations = {
  [types.SET_KANBAN_UI_FLAG](_state, data) {
    _state.uiFlags = {
      ..._state.uiFlags,
      ...data,
    };
  },

  [types.SET_KANBAN_BOARDS]: MutationHelpers.set,
  [types.SET_CURRENT_KANBAN_BOARD](_state, data) {
    _state.currentBoard = data;
  },
  [types.SET_CURRENT_KANBAN_BOARD_DATA](_state, data) {
    _state.currentBoardData = data;
  },
  [types.ADD_KANBAN_BOARD]: MutationHelpers.create,
  [types.EDIT_KANBAN_BOARD]: MutationHelpers.update,
  [types.DELETE_KANBAN_BOARD]: MutationHelpers.destroy,

  [types.ADD_KANBAN_COLUMN](_state, column) {
    if (_state.currentBoardData && _state.currentBoardData.columns) {
      _state.currentBoardData.columns.push(column);
    }
  },

  [types.EDIT_KANBAN_COLUMN](_state, updatedColumn) {
    if (_state.currentBoardData && _state.currentBoardData.columns) {
      const index = _state.currentBoardData.columns.findIndex(
        col => col.id === updatedColumn.id
      );
      if (index !== -1) {
        _state.currentBoardData.columns.splice(index, 1, updatedColumn);
      }
    }
  },

  [types.DELETE_KANBAN_COLUMN](_state, columnId) {
    if (_state.currentBoardData && _state.currentBoardData.columns) {
      _state.currentBoardData.columns = _state.currentBoardData.columns.filter(
        col => col.id !== columnId
      );
    }
  },

  [types.REORDER_KANBAN_COLUMNS](_state, positions) {
    if (_state.currentBoardData && _state.currentBoardData.columns) {
      positions.forEach(pos => {
        const column = _state.currentBoardData.columns.find(
          col => col.id === pos.id
        );
        if (column) {
          column.position = pos.position;
        }
      });
      _state.currentBoardData.columns.sort((a, b) => a.position - b.position);
    }
  },

  [types.MOVE_KANBAN_CONVERSATION](_state, { columnId, conversationId }) {
    if (_state.currentBoardData && _state.currentBoardData.columns) {
      // Encontrar la conversación en todas las columnas
      let conversation = null;

      _state.currentBoardData.columns.some(column => {
        const index = column.conversations?.findIndex(
          conv => conv.id === conversationId
        );
        if (index !== undefined && index !== -1) {
          conversation = column.conversations[index];
          // Remover de la columna origen
          column.conversations.splice(index, 1);
          column.conversation_count -= 1;
          return true; // Detener la búsqueda
        }
        return false;
      });

      // Agregar a la columna destino
      if (conversation) {
        const targetColumn = _state.currentBoardData.columns.find(
          col => col.id === columnId
        );
        if (targetColumn) {
          if (!targetColumn.conversations) {
            targetColumn.conversations = [];
          }
          targetColumn.conversations.unshift(conversation);
          targetColumn.conversation_count += 1;
        }
      }
    }
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
